import React, { useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import useSWR from 'swr';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Layout, LoadingSpinner, ErrorMessage } from '@/shared/components';
import { useNotificationActions } from '@/shared/stores/notification-store';
import { pricesApi } from '@/shared/api';
import { DraggablePriceItemRow } from '@/features/prices/DraggablePriceItemRow';
import type { PriceItemWithRelations } from '@/shared/types';

/**
 * Страница управления услугами в категории
 * Отображает список услуг конкретной категории с возможностью управления
 */
export const CategoryServicesPage: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const categoryIdNumber = categoryId ? parseInt(categoryId, 10) : 0;

  const [includeInactive, setIncludeInactive] = useState(false);
  const [localItems, setLocalItems] = useState<PriceItemWithRelations[]>([]);
  const [isUpdatingSortOrder, setIsUpdatingSortOrder] = useState(false);

  // Хуки для уведомлений
  const { showSuccess, showError } = useNotificationActions();

  // Загрузка категории
  const {
    data: categoryResponse,
    error: categoryError,
    isLoading: isLoadingCategory
  } = useSWR(
    categoryIdNumber ? ['price-category', categoryIdNumber] : null,
    () => pricesApi.getCategoryById(categoryIdNumber),
    { revalidateOnFocus: false }
  );

  // Загрузка услуг категории
  const {
    data: itemsResponse,
    error: itemsError,
    isLoading: isLoadingItems,
    mutate: mutateItems
  } = useSWR(
    categoryIdNumber
      ? ['price-items-by-category', categoryIdNumber, includeInactive]
      : null,
    () =>
      pricesApi.getAllItems({ categoryId: categoryIdNumber, includeInactive }),
    { refreshInterval: 30000 }
  );

  const category = categoryResponse?.category;
  const items = itemsResponse?.items || [];

  // Синхронизация локального состояния с данными из API
  React.useEffect(() => {
    if (items.length > 0) {
      setLocalItems(items);
    }
  }, [items]);

  /**
   * Форматирование цены
   */
  const formatPrice = (price: string | null): string => {
    if (price === null || price === '') return '—';
    const numPrice = Number(price);
    if (isNaN(numPrice)) return price; // Возвращаем как есть, если не число
    return `${numPrice.toLocaleString('ru-RU')} ₽`;
  };

  /**
   * Обработчик перемещения услуги в списке
   */
  const handleMoveItem = useCallback(
    (dragIndex: number, hoverIndex: number): void => {
      const draggedItem = localItems[dragIndex];
      if (!draggedItem) return;

      const newItems = [...localItems];

      // Удаляем элемент из старой позиции
      newItems.splice(dragIndex, 1);
      // Вставляем элемент в новую позицию
      newItems.splice(hoverIndex, 0, draggedItem);

      setLocalItems(newItems);
    },
    [localItems]
  );

  /**
   * Обработчик завершения перетаскивания - обновление sortOrder на сервере
   */
  const handleDragEnd = useCallback(async (): Promise<void> => {
    if (isUpdatingSortOrder) return;

    setIsUpdatingSortOrder(true);

    try {
      // Создаем массив обновлений с новыми sortOrder
      const updates = localItems.map((item, index) => ({
        id: item.id,
        sortOrder: index + 1 // sortOrder начинается с 1
      }));

      // Отправляем обновления на сервер
      await pricesApi.updateItemsSortOrder(updates);

      // Обновляем данные из API
      await mutateItems();

      showSuccess('Порядок услуг успешно обновлен');
    } catch (error) {
      console.error('Error updating items sort order:', error);
      // Возвращаем исходный порядок при ошибке
      setLocalItems(items);
      showError('Ошибка при обновлении порядка услуг');
    } finally {
      setIsUpdatingSortOrder(false);
    }
  }, [localItems, items, mutateItems, isUpdatingSortOrder]);

  /**
   * Обработчик изменения статуса услуги
   */
  const handleItemStatusChange = async (
    item: PriceItemWithRelations
  ): Promise<void> => {
    try {
      await pricesApi.updateItemStatus(item.id, !item.isActive);
      await mutateItems();
      showSuccess(
        `Услуга "${item.name}" успешно ${item.isActive ? 'деактивирована' : 'активирована'}`
      );
    } catch (error) {
      console.error('Error updating item status:', error);
      showError('Ошибка при изменении статуса услуги');
    }
  };

  // Обработка ошибок загрузки
  if (categoryError || itemsError) {
    return (
      <Layout title="Ошибка">
        <ErrorMessage message="Ошибка загрузки данных. Попробуйте обновить страницу." />
      </Layout>
    );
  }

  // Состояние загрузки
  if (isLoadingCategory || isLoadingItems) {
    return (
      <Layout title="Загрузка...">
        <div className="flex justify-center py-8">
          <LoadingSpinner size="lg" text="Загрузка услуг..." />
        </div>
      </Layout>
    );
  }

  // Категория не найдена
  if (!category) {
    return (
      <Layout title="Категория не найдена">
        <div className="text-center py-8">
          <i className="fas fa-exclamation-triangle text-4xl text-warning mb-4"></i>
          <h2 className="text-xl font-semibold mb-2">Категория не найдена</h2>
          <p className="text-base-content/60 mb-4">
            Запрашиваемая категория не существует или была удалена.
          </p>
          <Link to="/prices" className="btn btn-primary">
            <i className="fas fa-arrow-left mr-2"></i>
            Вернуться к ценам
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={`Услуги: ${category.name}`}>
      <div className="space-y-6">
        {/* Breadcrumb навигация */}
        <div className="breadcrumbs text-sm">
          <ul>
            <li>
              <Link to="/prices" className="link link-hover">
                <i className="fas fa-tags mr-1"></i>
                Цены
              </Link>
            </li>
            <li>
              <span className="text-base-content/60">{category.name}</span>
            </li>
          </ul>
        </div>

        {/* Заголовок и кнопки */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-base-content">
              Услуги категории "{category.name}"
            </h2>
            <p className="text-base-content/60 mt-1">
              Управление услугами в категории
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/prices" className="btn btn-ghost">
              <i className="fas fa-arrow-left mr-2"></i>
              Назад к категориям
            </Link>
            <Link
              to="/prices/items/create"
              className="btn btn-primary"
              state={{ categoryId: categoryIdNumber }}
            >
              <i className="fas fa-plus mr-2"></i>
              Добавить услугу
            </Link>
          </div>
        </div>

        {/* Фильтры */}
        <div className="card card-bordered bg-base-100 shadow-sm">
          <div className="card-body p-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="includeInactive"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                className="checkbox checkbox-primary"
              />
              <label htmlFor="includeInactive" className="text-sm font-medium">
                Показать неактивные услуги
              </label>
            </div>
          </div>
        </div>

        {/* Список услуг */}
        <div className="card card-bordered bg-base-100 shadow-sm">
          <div className="card-body p-4">
            <h3 className="text-lg font-semibold text-base-content mb-4">
              Услуги ({items.length})
            </h3>

            {localItems.length === 0 ? (
              <div className="text-center py-8">
                <i className="fas fa-clipboard-list text-4xl text-base-content/30 mb-4"></i>
                <p className="text-base-content/60">
                  {includeInactive
                    ? 'В этой категории нет услуг'
                    : 'В этой категории нет активных услуг'}
                </p>
                <Link
                  to="/prices/items/create"
                  className="btn btn-primary mt-4"
                  state={{ categoryId: categoryIdNumber }}
                >
                  <i className="fas fa-plus mr-2"></i>
                  Добавить первую услугу
                </Link>
              </div>
            ) : (
              <DndProvider backend={HTML5Backend}>
                <div className="overflow-x-auto">
                  <table className="table table-zebra w-full">
                    <thead>
                      <tr>
                        <th className="w-8">
                          <i
                            className="fas fa-grip-vertical text-base-content/40"
                            title="Перетащите для изменения порядка"
                          ></i>
                        </th>
                        <th>Название</th>
                        <th>Врач</th>
                        <th>Цена очно</th>
                        <th>Цена онлайн</th>
                        <th>Статус</th>
                        <th>Порядок</th>
                        <th>Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {localItems.map(
                        (item: PriceItemWithRelations, index: number) => (
                          <DraggablePriceItemRow
                            key={item.id}
                            item={item}
                            index={index}
                            onMove={handleMoveItem}
                            onDragEnd={handleDragEnd}
                            onStatusChange={handleItemStatusChange}
                            formatPrice={formatPrice}
                          />
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </DndProvider>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};
